import { useState, useRef, useEffect, useCallback } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { router } from "@inertiajs/react"
import { Check, CheckCircle2, Clock, Copy, MailPlus, Play, Search, Sparkles } from "lucide-react"
import { Layout } from "@/components/layouts/Layout"
import { ScrollArea } from "@/components/ui/scroll-area"
import { DataTableReact } from "@/components/ui/data-table-react"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { RunsChart } from "./RunsChart"
import { AgentSparkline } from "./AgentSparkline"

function timeAgo(dateString) {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function agentStatusColor(status) {
  switch (status) {
    case "active":   return "bg-green-500"
    case "paused":   return "bg-amber-500"
    case "stopped":  return "bg-red-500"
    default:         return "bg-muted-foreground/40"
  }
}

function CopyableEmailAddress({ email }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [email])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group/copy flex w-full items-center justify-between gap-4 rounded-lg bg-muted px-3 py-2.5 transition-colors hover:bg-muted/70 cursor-pointer"
    >
      <span className="min-w-0 truncate font-mono text-sm">{email}</span>
      <span className="relative text-[13px] text-slate-500 transition-colors group-hover/copy:text-foreground shrink-0">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={copied ? "copied" : "copy"}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1"
          >
            {copied ? (
              <>
                <Check className="size-3.5 text-green-600" />
                <span className="text-green-600">Copied</span>
              </>
            ) : (
              <>
                <Copy className="size-3.5" />
                <span>Copy</span>
              </>
            )}
          </motion.span>
        </AnimatePresence>
      </span>
    </button>
  )
}

const columns = [
  {
    header: "Agent",
    accessorKey: "name",
    size: 300,
    cell: (value, row) => (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className={`size-2 rounded-full shrink-0 ${agentStatusColor(row.status)}`} />
          <span className="font-medium">{value || "Untitled agent"}</span>
        </div>
        <span className="text-xs text-muted-foreground capitalize pl-4">{row.status}</span>
      </div>
    ),
  },
  {
    header: "Runs (30d)",
    accessorKey: "runs_last_30_days",
    size: 260,
    cell: (value, row) => (
      <div className="flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-muted-foreground tabular-nums">
          <Play className="size-3.5 shrink-0" />
          {value} {value === 1 ? "run" : "runs"}
        </span>
        <AgentSparkline data={row.sparkline_30d || []} />
      </div>
    ),
  },
  {
    header: (
      <Tooltip>
        <TooltipTrigger asChild><span>Success rate</span></TooltipTrigger>
        <TooltipContent>Success rate over the last 30 days</TooltipContent>
      </Tooltip>
    ),
    accessorKey: "success_rate_30_days",
    size: 200,
    cell: (value) => {
      if (value === null) return (
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <CheckCircle2 className="size-3.5 shrink-0" />
          —
        </span>
      )
      const { label, variant } = value >= 90
        ? { label: "High",   variant: "success" }
        : value >= 70
        ? { label: "Medium", variant: "pending" }
        : { label: "Low",    variant: "warning" }
      return (
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <CheckCircle2 className="size-3.5 shrink-0" />
            {value}%
          </span>
          <Badge variant={variant} size="small" className={variant === "pending" ? "[tr:hover_&]:bg-white dark:[tr:hover_&]:bg-slate-700 duration-300 ease-in-out" : undefined}>{label}</Badge>
        </span>
      )
    },
  },
  {
    header: "Last run",
    accessorKey: "last_run_at",
    size: 160,
    cell: (value) => (
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Clock className="size-3.5 shrink-0" />
        {value ? timeAgo(value) : "Never"}
      </span>
    ),
  },
]

export default function AgentsIndex({ agents, chart_runs = [], workspace_slug }) {
  const [search, setSearch] = useState("")
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  const filteredAgents = search.trim()
    ? agents.filter((a) => (a.name || "").toLowerCase().includes(search.toLowerCase()))
    : agents

  return (
    <Layout insetClassName="overflow-hidden">

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>
        <div className="flex items-center gap-1">
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium truncate">Agents</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>
        <div className="flex flex-col px-6 pb-6 gap-8">

          <h1 ref={nameRef} className="text-3xl font-medium">Agents</h1>

          {agents.length > 0 && <RunsChart runs={chart_runs} />}

          <section className="flex flex-1 flex-col gap-3">
            {agents.length > 0 && (
              <div className="flex items-end justify-between">
                <h2 className="text-md font-medium flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4 text-muted-foreground" />
                  Agents
                </h2>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Search agents..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 h-8 w-48 text-sm"
                  />
                </div>
              </div>
            )}

            {filteredAgents.length > 0 ? (
              <DataTableReact
                columns={columns}
                data={filteredAgents}
                sortable
                resizable
                onRowClick={(row) => router.visit(`/${workspace_slug}/agents/${row.id}`)}
              />
            ) : agents.length > 0 ? (
              <p className="text-muted-foreground">No agents match your search.</p>
            ) : (
              <div className="flex flex-col items-center py-16 text-center gap-8 max-w-sm mx-auto">
                <div className="rounded-full bg-muted p-4">
                  <MailPlus className="h-6 w-6 text-muted-foreground" />
                </div>

                <div className="flex flex-col gap-1.5">
                  <h3 className="text-xl font-medium">Create your first agent</h3>
                  <p className="text-sm text-muted-foreground text-pretty">
                    Agents monitor incoming emails, extract data, and route it where requested.
                  </p>
                </div>

                <div className="flex flex-col gap-2 w-full">
                  <p className="text-sm text-muted-foreground text-left">Send an email to get started:</p>
                  <CopyableEmailAddress email="new@app.decodekit.ai" />
                </div>

                <div className="flex flex-col gap-4 w-full text-left">
                  {[
                    {
                      step: "1",
                      title: "Send an email",
                      description: "Forward an email you want to create an agent from",
                    },
                    {
                      step: "2",
                      title: "Review and configure",
                      description: "DecodeKit creates a draft agent",
                    },
                    {
                      step: "3",
                      title: "Activate your agent",
                      description: "Confirm the draft and where to send the data",
                    },
                  ].map(({ step, title, description }) => (
                    <div key={step} className="flex items-start gap-3">
                      <div className="shrink-0 rounded-full bg-muted size-7 flex items-center justify-center text-sm font-medium text-muted-foreground -mt-0.5">
                        {step}
                      </div>
                      <div className="flex flex-col">
                        <p className="text-base font-medium">{title}</p>
                        <p className="text-sm text-muted-foreground">{description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

        </div>
      </ScrollArea>

    </Layout>
  )
}
